export const BASE_URL = import.meta.env.VITE_BASE_URL;
export const ENV_NAME = import.meta.env.VITE_ENV_NAME;

export const makeUrl = (path, query = {}) => {
  const queryString = Object.entries(query || {})
    .map(([key, value]) => {
      const v = encodeURIComponent(value?.toString());
      return `${key}=${v}`;
    })
    .join('&');
  return queryString ? `${ENV_NAME}${path}?${queryString}` : `${ENV_NAME}${path}`;
};

export const Url = {
  actions: {
    adhoc: () => makeUrl('/action_instances/adhoc'),
    educationActions: () => makeUrl('/education_actions'),
    patientMetrics: () => makeUrl('/action_instances/patient_metrics'),
    templates: () => makeUrl('/action_templates'), // This was `pathwayInstanceEndpoint`
  },
  actionDetails: {
    education: (id) => makeUrl(`/education_actions/${id}`),
    instruction: (id) => makeUrl(`/instructions/${id}`),
    navigation: (id) => makeUrl(`/navigations/${id}`),
  },
  administrators: () => makeUrl('/administrators'),
  updateAdministrator: (id) => makeUrl(`/administrators/${id}`),
  bootstrap: {
    pathwayTemplate: () => makeUrl('/bootstrap/pathway_template'),
  },
  consent: (url) => makeUrl(`/consent${url}`),
  consentBaseUrl: () => makeUrl('/consent'),
  consentSource: (params) => makeUrl('/consent_source', params),
  deviceById: () => makeUrl('/device'),
  instructions: (params) => makeUrl('/instructions', params),
  memberships: {
    organizations: () => makeUrl('/memberships/organizations'),
  },
  mhc: {
    all: () => makeUrl('/medical_history_and_conditions'),
    bulkSearch: () => makeUrl('/medical_history_and_conditions/patient_bulk_code_search'), // POST
    update: (medical_history_and_conditions_id) =>
      makeUrl(`/medical_history_and_conditions/${medical_history_and_conditions_id}`),
  },
  navigations: (params) => makeUrl('/navigations', params),
  organizations: {
    all: () => makeUrl('/organizations'),
    search: () => makeUrl('/organizations/search'),
    update: (id) => makeUrl(`/organizations/${id}`),
    updateOnboardingStep: (id) => makeUrl(`/organizations/${id}/onboarding_step`),
  },
  patientLists: {
    execute: () => makeUrl('/patient_lists/execute'),
  },
  patients: {
    all: () => makeUrl('/patients'),
    one: (patientId) => makeUrl(`/patients/${patientId}`),
    invite: (patientId) => makeUrl(`/patients/${patientId}/invite`),
    medications: (patientId) => makeUrl(`/patients/${patientId}/medications`),
    byOrganization: (organizationId) =>
      makeUrl(`/patients?limit=1&organizationId=${organizationId}`),
    getFiles: (params) => makeUrl(`/patients/${params.patientId}/files`, params),
    actionFile: ({ patientId, fileId }) => makeUrl(`/patients/${patientId}/files/${fileId}`),
    creatPresignedUrlS3: ({ patientId }) => makeUrl(`/patients/${patientId}/files`),
    activityLog: (params) => makeUrl('/activity_log', params),
  },
  pathway: {
    instances: () => makeUrl('/pathway_instances'),
    listInstances: (params) => makeUrl(`/pathway_instances`, params),
    instanceById: (id) => makeUrl(`/pathway_instances/${id}`),
    templateById: (id) => makeUrl(`/pathway_templates/${id}`),
    templates: (params) => makeUrl('/pathway_templates', params),
  },
  personas: {
    search: () => makeUrl('/personas/search'),
  },
  persons: () => makeUrl('/persons'),
  practitioners: (params) => makeUrl('/practitioners', params),
  practitionersReferral: (params) => makeUrl('/practitioners/referral', params),
  externalPractitioners: (npi) => makeUrl(`/npi_registry/nppes/${npi}`),
  solutions: {
    all: () => makeUrl('/solutions'),
    patients: () => makeUrl('/solutions/patients'),
    pathwayTemplates: () => makeUrl('/solutions/pathway_templates'),
  },
  educationActions: (params) => makeUrl('/education_actions', params),
  surveys: (params) => makeUrl('/surveys', params),
  survey: (id) => makeUrl(`/surveys/${id}`),
  surveyResponses: {
    all: (params) => makeUrl('/survey_responses', params),
    search: {
      search: () => makeUrl('/survey_responses/search'),
      linkId: () => makeUrl('/survey_responses/search/link_id'),
    },
  },
  therapy: {
    all: () => makeUrl('/therapy_data'),
    bloodPressure: {
      average: (params) => makeUrl('/therapy_data/blood_pressure/average', params),
      averageOverTimeframe: (params) =>
        makeUrl('/therapy_data/blood_pressure/average_over_timeframe', params),
      bp: () => makeUrl('/therapy_data/blood_pressure'),
      systolicTarget: () => makeUrl('/therapy_data/blood_pressure/systolic_target'),
    },
    bulkSearch: () => makeUrl('/therapy_data/patient_bulk_code_search'), // POST
    dailyVariability: (params) => makeUrl('/therapy_data/daily_variability', params),
    search: (params) => makeUrl('/therapy_data/search', params),
    updateOrDelete: (therapy_data_id) => makeUrl(`/therapy_data/${therapy_data_id}`),
    snoozePatient: () => makeUrl('/therapy_data/schema'),
    healthkitQuantitiesStatistics: () =>
      makeUrl('/healthkit/quantities/statistics/bucketed_over_timeframe'),
  },
  users: {
    activate: () => makeUrl('/users/activate_with_code'),
    all: () => makeUrl('/users'),
    me: () => makeUrl('/users/me'),
  },
  careTeam: {
    getPatients: (params) => makeUrl(`/patients/${params?.patientId}/care_team`, params),
    addMemberToCareTeam: () => makeUrl(`/careteam`),
    removeMemberFromCareTeam: (relationId) => makeUrl(`/careteam/${relationId}`),
  },
  checkInviteUserEligibility: ({ userTypePath, userId }) =>
    makeUrl(`${userTypePath}/${userId}/check_invite_eligibility`),
  inviteUser: ({ userTypePath, userId }) => makeUrl(`${userTypePath}/${userId}/invite`),
  icReferral: {
    createForm: () => makeUrl(`/ic_referral_form`),
    updateForm: (formId) => makeUrl(`/ic_referral_form/${formId}`),
  },
  patientEnrollmentEmr: {
    getOrders: (params) => makeUrl('/order_request', params),
    getOrder: (orderId) => makeUrl(`/order_request/${orderId}`),
    updateOrders: (orderId) => makeUrl(`/order_request/${orderId}`),
  },
  patientPathway: {
    triggerIOSTask: (params) => makeUrl('/trigger_ios_task', params),
    removeIOSRdnTasks: (params) => makeUrl('/action_instances/remove_ios_rdn_tasks', params),
  },
  pods: {
    getPods: (params) => makeUrl('/pods', params),
    reassignPatient: (patientId) => makeUrl(`/pods/membership/patients/${patientId}`),
  },
  rdnScreening: {
    getOrganizationRdnScreeningChecklist: ({ organizationId }) =>
      makeUrl(`/rdn_screening/organizations/${organizationId}`),
    updateOrganizationRdnScreeningChecklist: ({ organizationId }) =>
      makeUrl(`/rdn_screening/organizations/${organizationId}`),
  },
};
